// src/lib/partners.js
// Migrated to Worker API
import { createApi } from './api';

// Reusing the same generic billing/checkout endpoints as Credits,
// but passing specific payload. Worker handles logic.
// For Partner Subscription, pass mode='subscription' (default).

export const PARTNER_PRICES = {
  // These will be resolved at runtime using stripePrices helper
  // or passed directly if already known.
  // For now we keep an empty map or placeholders if needed.
  // The UI calls startPartnerCheckout({ priceId: '...' });
};

/**
 * Get the current user's partner document/subscription status.
 * @param {function} getToken - Function to get the current auth token
 */
export async function getMyPartnerDoc(getToken) {
  if (!getToken) return null;
  const api = createApi(getToken);
  try {
    // Try to fetch partner status from backend
    const res = await api.get('/partners/me');
    if (res && !res.error) {
      return res;
    }
    return null;
  } catch (err) {
    console.error("Error fetching partner doc:", err);
    return null;
  }
}

/**
 * Ensure the partner document exists with the provided data.
 * @param {object} data - Profile data (fullName, businessName, etc.)
 * @param {function} getToken - Function to get the current auth token
 */
export async function ensurePartnerDoc(data, getToken) {
  if (!getToken) throw new Error("Authentication required");
  const api = createApi(getToken);
  
  // Post to an endpoint that updates or creates the partner profile
  const res = await api.post('/partners/profile', data);
  
  if (res.error) throw new Error(res.error);
  return res;
}

/**
 * Start Stripe Checkout for a partner plan.
 * Uses the generic /api/billing/checkout endpoint.
 */
export async function startPartnerCheckout({ priceId, mode = 'subscription', successUrl, cancelUrl }, token) {
  if (!priceId) throw new Error('Missing priceId');
  if (!token) throw new Error('Must be signed in');

  const api = createApi(() => Promise.resolve(token));
  
  // Use success/cancel URLs specific to partners if desired,
  // otherwise Worker defaults to /billing?success=1
  // We want /partner/profile usually.
  const payload = { 
    priceId, 
    mode,
    successUrl: successUrl || (window.location.origin + "/partner/profile?success=true"),
    cancelUrl: cancelUrl || (window.location.origin + "/partners/subscribe?canceled=true")
  };

  const res = await api.post('/billing/checkout', payload);

  if (!res || res.error) throw new Error(res?.error || 'Checkout failed');
  if (res.url) {
      window.location.href = res.url;
      return res.url;
  }
  throw new Error('No checkout URL returned');
}

/**
 * Open Stripe Billing Portal.
 * Uses the generic /api/billing/portal endpoint.
 */
export async function openPartnerBillingPortal(token) {
    if (!token) throw new Error('Must be signed in');
    const api = createApi(() => Promise.resolve(token));
    const res = await api.post('/billing/portal', {
        returnUrl: window.location.origin + "/partner/profile"
    });
    if (res && res.url) {
        window.location.href = res.url;
        return res.url;
    }
    throw new Error('Could not open billing portal');
}
